<template>
  <div class="page">
    <div class="container">
      <div class="search-wrapper" :class="{ moved }" v-if="!certificate.CertNum">
        <div class="search-input">
          <input type="text" v-model="searchVal" placeholder="Enter Your Report No." inputmode="numeric"
            autocomplete="off" />

          <button class="search" :disabled="!searchVal" @click="searchItem">
            Verify
          </button>
        </div>

        <p class="help">
          If you are experiencing issues retrieving a digital certificate,
          please contact Diamond Services USA for assistance.
        </p>
      </div>

      <div class="results" v-else>
        <div class="tabs" role="tablist">
          <button class="tab" role="tab" :aria-selected="selectedTab === 'Digital'" @click="selectedTab = 'Digital'"
            :class="{ active: selectedTab === 'Digital' }">
            Digital Certificate
          </button>
          <button class="tab" role="tab" :aria-selected="selectedTab === 'Card'" @click="selectedTab = 'Card'"
            :class="{ active: selectedTab === 'Card' }">
            Summary
          </button>
          <button class="tab" role="tab" @click="clearFilter">Search</button>
        </div>

        <div v-if="selectedTab === 'Digital'" class="digital-wrapper">
          <div class="frame-wrap">
            <iframe class="cert-frame"
              :src="`https://certificates.diamondservicesusa.com/jewelry/${certificate.CertNum}`" frameborder="0"
              title="Digital certificate"></iframe>
          </div>
        </div>

        <div v-if="selectedTab === 'Card'">
          <CardCert :certificate="certificate" :certImage="certImage" :isHelzberg="isHelzberg" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { db, functions } from "@/config/firebaseInit";
import Swal from "sweetalert2";
import CardCert from "../components/CardCert.vue";
import { resolvePngOrJpg } from "@/services/dropbox";

export default {
  components: { CardCert },
  data() {
    return {
      searchVal: "",
      results: [],
      certificate: {},
      itemType: "",
      selectedTab: "Digital",
      certImage: null,
      fileExtPath: "jpg",
      fileNameOptions: {
        CertNum: false,
        ClientSKU: false,
      },
      moved: false, // <-- was missing; used by search wrapper
    };
  },
  computed: {
    isHelzberg() {
      // guard against undefined on initial render
      return !!(this.certificate?.Company?.name || "").includes("Helzberg");
    },
    dropboxCertPath() {
      return this.isHelzberg
        ? "/Helzberg/IMAGES/"
        : "/Card-Certificate Images/";
    },
  },
  methods: {
    openInWindow() {
      window.open(
        `https://www.certificates.diamondservicesusa.com/jewelry/${this.certificate.CertNum}`,
        "_blank",
        "noopener,noreferrer"
      );
    },
    async searchItem() {
      let query = [];
      let value = this.searchVal.replace(/^[a-zA-Z\s]+|^#|[^0-9]/g, "");
      this.itemType = "diamond";
      let data;
      if (value) {
        this.moved = true;
        const loader = this.$loading.show({
          container: null,
          canCancel: false,
        });
        try {
          query = await db
            .collection("diamond")
            .where("CertNum", "==", value)
            .get();
          if (query.empty) {
            this.itemType = "jewelry";
            query = await db
              .collection("jewelry")
              .where("CertNum", "==", value)
              .get();
          }

          const searchDiamondFromAPI = functions.httpsCallable(
            "searchDiamondFromAPI"
          );
          const searchJewelryFromAPI = functions.httpsCallable(
            "searchJewelryFromAPI"
          );

          data =
            this.itemType === "diamond"
              ? await searchDiamondFromAPI(value)
              : await searchJewelryFromAPI(value);

          data = data.data;

          if (data.Message === "Success") {
            this.results = query.docs.map((d) =>
              Object.assign(
                { id: d.id, certType: data.CertDetail.CertType },
                d.data()
              )
            );
            this.certificate = this.results[0];
            await this.resolveDropboxImages("ClientSKU");
          } else {
            await Swal.fire(
              "",
              "The certificate number you entered does not exists. Please try another certificate number. If this is an error and certificate does exists please contact DIS USA support for assistance",
              "info"
            );
            this.searchVal = "";
          }
        } catch (error) {
          console.log(error);
          await Swal.fire(
            "Error:",
            error.message + " Please try again later",
            "error"
          );
        } finally {
          loader?.hide?.();
        }
      } else {
        this.searchVal = null;
      }
    },
    async resolveDropboxImages(key) {
      const value = this.certificate[key];
      if (!value) return null;

      const base = `${this.dropboxCertPath}${value}`;
      try {
        const link = await resolvePngOrJpg(base);
        this.certImage = link;
        return link;
      } catch (err) {
        this.fileNameOptions[key] = true;
        if (this.fileNameOptions.CertNum && this.fileNameOptions.ClientSKU)
          return null;
        return this.resolveDropboxImages("CertNum");
      }
    },
    clearFilter() {
      this.certificate = {};
      this.moved = false;
    },
  },
  watch: {
    searchVal(val) {
      if (!val) this.results = [];
    },
  },
};
</script>

<style lang="scss" scoped>
/* Fluid scale + spacing */
:root {
  --space-1: clamp(0.5rem, 1.5vw, 0.75rem);
  --space-2: clamp(0.75rem, 2.2vw, 1rem);
  --space-3: clamp(1rem, 3vw, 1.5rem);
  --radius: 16px;
  --text-base: clamp(14px, 1.8vw, 16px);
  --text-lg: clamp(16px, 2.4vw, 20px);
}

/* Page wrapper uses small viewport height to avoid iOS 100vh bugs */
.page {
  min-height: 100svh;
  background-color: transparent;
}

.container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-2);
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;

  input {
    padding: 0.6rem 0.8rem;
    font-size: var(--text-base);
    border: 1px solid rgba(147, 144, 144, 0.6);
    border-radius: 10px;
    flex: 1 1 auto;
    min-width: 0;
  }

  input::placeholder {
    color: #000;
    opacity: 0.5;
  }
}

.help {
  color: #fff;
  // background: #465b5d;
  padding: var(--space-2);
  border-radius: 12px;
  margin: 0;
  font-size: clamp(13px, 1.6vw, 15px);
  padding: 1rem;
}

.results {
  width: 100%;
  /* let the content define height; avoid hard 105vh */
  padding: var(--space-2);
  box-sizing: border-box;
}

/* Tabs become scrollable on small screens */
.tabs {
  width: 100%;
  display: flex;
  gap: 6px;
  margin-bottom: var(--space-2);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 4px;
}

.tab {
  flex: 1 1 160px;
  white-space: nowrap;
  background: transparent;
  border: 1px solid rgba(187, 185, 185, 0.76);
  border-radius: 999px;
  padding: 8px 12px;
  text-align: center;
  font-size: var(--text-base);
  color: rgb(91, 90, 90);
  cursor: pointer;
}

.tab:hover:not(.active) {
  background-color: #eef2f4;
}

.tab.active {
  background-color: #465b5d;
  color: #fff;
}

/* Iframe becomes fluid with a max width, and keeps aspect ratio */
.digital-wrapper {
  width: 100%;
  display: grid;
  gap: var(--space-2);
  justify-items: center;
}

.frame-wrap {
  width: min(100%, 720px);
  /* maintain a pleasant ratio on mobile; auto-sizes height */
  height: 100dvh;
  background: #fff;
  border-radius: var(--radius);
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

@media screen and (min-width: 769px) {
  .frame-wrap {
    width: 390px;
    height: 100dvh;
  }
}

.cert-frame {
  width: 100%;
  height: 100%;
  display: block;
  border: 0;
}

/* Buttons */
.search {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid #6ec1e4;
  background: #6ec1e4;
  color: #fff;
  font-weight: 600;
  font-size: var(--text-base);
  cursor: pointer;
}

.search:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.open-window {
  width: min(100%, 720px);
}

/* Search box in the hero area */
.search-wrapper {
  position: fixed;
  inset-inline: 0;
  top: 50%;
  transform: translateY(-50%);
  display: grid;
  gap: var(--space-2);
  justify-items: center;
  padding: 2rem;
  transition: transform 0.4s ease, top 0.4s ease;
  z-index: 100;
  padding-top: 8rem;
}

.search-wrapper.moved {
  top: max(12px, env(safe-area-inset-top));
  transform: translateY(0);
}

.search-input {
  width: min(100%, 720px);
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  margin-bottom: 2rem;
}

/* Small screens */
@media (max-width: 600px) {
  .results {
    padding: var(--space-2);
  }

  .search-input {
    grid-template-columns: 1fr;
    /* stack input & button */
  }

  .open-window {
    width: 100%;
  }
}

/* Larger screens */
@media (min-width: 900px) {
  .tabs {
    gap: 10px;
  }
}

/* Respect motion settings */
@media (prefers-reduced-motion: reduce) {
  .search-wrapper {
    transition: none;
  }
}
</style>
